import { ResourcePlanOverviewFilters } from "@src/services/resourcePlanOverviewSlice";
import { ResourcePlanOverviewTableRowData } from "@src/types/resource_plan_types";
import { DigixPhase, DigixProject } from "@src/types/project_types";
import { IRowNode } from "ag-grid-community";
import _ from "lodash";

/**
 * Checks if the row should be displayed based on the quick filter and custom table filters
 * @param quickFilter the quick filter string to match against
 * @param node the row node containing the data for the master row
 * @param customTableFilters the custom filters that are applied to the table
 */
export function onFilter(
  quickFilter: string,
  node: Partial<IRowNode<ResourcePlanOverviewTableRowData>>,
  customTableFilters: ResourcePlanOverviewFilters
): boolean {
  const projects: Partial<DigixProject>[] = node?.data?.projects;

  // --- QUICK TEXT FILTER ---
  if (quickFilter && quickFilter.trim() !== "") {
    return onQuickFilterChanged(node, quickFilter);
  }

  // --- CUSTOM FILTERS ---
  if (customTableFilters) {
    return onCustomFilterChanged(projects, customTableFilters);
  }

  return true;
}

/**
 * Checks if the custom filters match the projects in the master row
 * @param projects the projects in the master row
 * @param customTableFilters the custom filters that are applied to the table
 */
function onCustomFilterChanged(projects: Partial<DigixProject>[], customTableFilters: ResourcePlanOverviewFilters) {
  const filterNames: string[] = Object.keys(customTableFilters);

  for (const filterName of filterNames) {
    const filterValues = customTableFilters[filterName];
    if (typeof filterValues === "boolean") continue; // skip boolean filters

    if (filterValues.length > 0) {
      const matchFound = projects.some((project: Partial<DigixProject>) => {
        if (filterName === "project") {
          return filterValues.includes(project.name);
        } else if (filterName === "phase") {
          const phaseNames = project.phases.map((phase: DigixPhase) => phase.name);
          return _.intersection(phaseNames, filterValues).length > 0;
        } else {
          return filterValues.includes(project[filterName]);
        }
      });

      if (!matchFound) return false;
    }
  }

  return true;
}

/**
 * Checks if the quick filter matches the master row or any detail rows
 *
 * @param node the row node containing the data for the master row
 * @param quickFilter the quick filter string to match against
 */
function onQuickFilterChanged(node: Partial<IRowNode<ResourcePlanOverviewTableRowData>>, quickFilter: string): boolean {
  const filter = quickFilter.toLowerCase();

  // Check master row
  const masterMatches: boolean = Object.values(node.data).some((value: number | string | Partial<DigixProject>[]) => {
    return value.toString().toLowerCase().includes(filter);
  });

  // Check detail rows (if any)
  const projects: Partial<DigixProject>[] = node.data.projects || [];
  const detailMatches = projects.some((project: Partial<DigixProject>) =>
    Object.values(project).some((value) => value?.toString().toLowerCase().includes(filter))
  );

  return masterMatches || detailMatches;
}
